import type { Ctx } from '@milkdown/kit/ctx';
import { $ctx } from '@milkdown/kit/utils';
import { editorViewCtx } from '@milkdown/kit/core';
import type { Node } from '@milkdown/kit/prose/model';
import type { EditorState } from '@milkdown/kit/prose/state';
import { TextSelection } from '@milkdown/kit/prose/state';
import type { EditorView } from '@milkdown/kit/prose/view';

import { ATTACHMENT_NODE_NAME } from '../../plugins/hocuspocus-platform-schema/attachment';
import { defaultMediaUpload } from '../../plugins/runEditorMediaUpload';
import type { MediaUploadHandler } from '../../types';
import { ACCEPTED_FILE_TYPES, isAcceptedAttachmentFile } from './acceptedFiles';

export { ACCEPTED_FILE_TYPES, isAcceptedAttachmentFile } from './acceptedFiles';

export interface AttachmentConfig {
  onUploadFile?: MediaUploadHandler;
}

export const attachmentConfig = $ctx<AttachmentConfig, 'attachmentConfig'>({ onUploadFile: undefined }, 'attachmentConfig');

/** Paste/drop: insert at cursor. Slash menu: replace the current block (e.g. empty `/file` paragraph). */
export type AttachmentInsertMode = 'atCursor' | 'replaceBlock';

function getAttachmentInsertRange(state: EditorState, mode: AttachmentInsertMode): { from: number; to: number } {
  const { $from, from } = state.selection;

  if (mode === 'replaceBlock' && $from.depth > 0) {
    return { from: $from.before($from.depth), to: $from.after($from.depth) };
  }

  return { from, to: from };
}

function clampInsertRange(state: EditorState, range: { from: number; to: number }): { from: number; to: number } {
  const size = state.doc.content.size;
  if (range.from >= 0 && range.to >= 0 && range.from <= size && range.to <= size) {
    return range;
  }

  return getAttachmentInsertRange(state, 'atCursor');
}

/** Insert attachment node at range; returns document position after the new node. */
function insertAttachmentNode(view: EditorView, node: Node, range: { from: number; to: number }): number {
  const { from, to } = clampInsertRange(view.state, range);
  let tr = view.state.tr.replaceWith(from, to, node);
  const afterPos = tr.mapping.map(from) + node.nodeSize;
  tr = tr.setSelection(TextSelection.near(tr.doc.resolve(afterPos), 1));
  view.dispatch(tr.scrollIntoView());
  view.focus();

  return afterPos;
}

/** Upload a file and insert an attachment node (no placeholder). */
export async function uploadAndInsertAttachment(
  view: EditorView,
  file: File,
  upload: MediaUploadHandler,
  options?: { mode?: AttachmentInsertMode; range?: { from: number; to: number } },
): Promise<number | undefined> {
  const src = await upload(file);
  if (view.isDestroyed) return;

  const attachmentNodeType = view.state.schema.nodes[ATTACHMENT_NODE_NAME];
  if (!attachmentNodeType) {
    console.error('[Attachment] attachment node type not found in schema');

    return;
  }

  const node = attachmentNodeType.create({
    id: null,
    href: src,
    title: file.name,
    size: String(file.size),
  });

  const range = options?.range ?? getAttachmentInsertRange(view.state, options?.mode ?? 'atCursor');

  return insertAttachmentNode(view, node, range);
}

/** Upload attachment files sequentially (paste/drop). */
export async function uploadAndInsertAttachments(
  view: EditorView,
  files: File[],
  upload: MediaUploadHandler,
  options?: { abortSignal?: AbortSignal },
): Promise<void> {
  for (const file of files) {
    if (!isAcceptedAttachmentFile(file)) continue;
    if (options?.abortSignal?.aborted || view.isDestroyed) return;

    try {
      await uploadAndInsertAttachment(view, file, upload);
    } catch (error) {
      console.error('[Attachment] Upload failed:', error);
    }
  }
}

/** Open native file picker and upload the selected attachment (slash menu). */
export function pickAndUploadAttachment(view: EditorView, upload: MediaUploadHandler = defaultMediaUpload): void {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = ACCEPTED_FILE_TYPES;
  input.style.display = 'none';

  const cleanupInput = () => input.remove();
  input.addEventListener('cancel', cleanupInput);

  input.addEventListener('change', () => {
    cleanupInput();
    const file = input.files?.[0];
    if (!file || !isAcceptedAttachmentFile(file)) return;

    void uploadAndInsertAttachment(view, file, upload, { mode: 'replaceBlock' }).catch((error) => {
      console.error('[Attachment] Upload failed:', error);
    });
  });

  document.body.appendChild(input);
  input.click();
}

export function pickAndUploadAttachmentFromCtx(ctx: Ctx): void {
  const view = ctx.get(editorViewCtx);
  const upload = ctx.get(attachmentConfig.key).onUploadFile ?? defaultMediaUpload;
  pickAndUploadAttachment(view, upload);
}
